import { PhotoIcon } from '@heroicons/react/24/outline';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
} from '@material-tailwind/react';
import React, { useMemo } from 'react';
import { numberWithCommas } from '../../../../../api/utils/format/numbers';
import TextWinds from '../../../../common/TextWinds';
import { Link } from 'react-router-dom';
import { BoostUpTypes } from '../../../../../constants/types';
import { current } from '@reduxjs/toolkit';
import { useAppSelector } from '../../../../../store/hook';
import { selectLmsLists } from '../../../../../store/features/lmsSlice';
import { CourseId } from '../../../../../constants/types/lms/type';

interface BoostupCardProps {
  data: BoostUpTypes;
  classId: string;
}

const COURSE_GOODS_PRICE = {
  KIDS: 420000,
  BASIC: 720000,
  ADVANCED: 190000,
} as const;

const BoostupCard = ({
  data: {
    id,
    name,
    origin_price,
    current_price,
    thumbnail,
    product_type,
    goods_type,
  },
  classId,
}: BoostupCardProps) => {
  const lmsLists = useAppSelector(selectLmsLists);

  const courseType = useMemo(() => {
    switch (lmsLists.course.id) {
      case CourseId.KIDS:
        return 'KIDS';
      case CourseId.BASIC:
        return 'BASIC';
      case CourseId.ADVANCED:
        return 'ADVANCED';
      default:
        return null;
    }
  }, [lmsLists]);

  const getMeta = () => {
    if (product_type !== 'COURSE') {
      return {
        path: `./boostup/detail/${id}`,
        currentPrice: numberWithCommas(current_price),
        originPrice: numberWithCommas(origin_price),
      };
    }

    if (goods_type === 'DIGITAL') {
      return {
        path: `${process.env.REACT_APP_JEBS_APP_URL}/courses/homeschool/details/${id}?option=false`,
        currentPrice: numberWithCommas(current_price),
        originPrice: numberWithCommas(origin_price),
      };
    }

    if (goods_type === 'GOOD') {
      const price =
        COURSE_GOODS_PRICE[courseType as keyof typeof COURSE_GOODS_PRICE] ?? 0;

      return {
        path: `${process.env.REACT_APP_JEBS_APP_URL}/courses/homeschool/details/${id}?option=true`,
        currentPrice: numberWithCommas(price),
        originPrice: numberWithCommas(price),
      };
    }

    return {
      path: '',
      currentPrice: 0,
      originPrice: 0,
    };
  };

  const meta = getMeta();

  const handleNewTabs = (path: string) => {
    window.open(path, '_blank');
  };

  return (
    <>
      <Card className="shadow-card">
        <Button
          className="h-full w-full rounded-xl border border-gray2 bg-white p-0 shadow-none hover:border-purple2"
          onClick={() => {
            if (product_type === 'COURSE') {
              if (goods_type === 'DIGITAL') {
                handleNewTabs(meta.path);
              } else if (goods_type === 'GOOD') {
                handleNewTabs(meta.path);
              } else {
                return;
              }
            } else {
              handleNewTabs(meta.path);
            }
          }}
        >
          <div className="flex h-full w-full flex-col">
            <CardHeader className="m-0 flex aspect-square items-center justify-center rounded-none rounded-t-xl bg-gray4 p-0 shadow-none">
              {thumbnail?.access_url ? (
                <img
                  className="h-full w-full object-cover"
                  src={thumbnail.access_url}
                  alt={name}
                />
              ) : (
                <PhotoIcon className="h-1/2 w-1/2 text-gray6" />
              )}
            </CardHeader>

            <CardBody className="flex flex-1 flex-col p-2">
              <div className="min-h-[42px]">
                <TextWinds
                  type="content_body3"
                  color="gray8"
                  className="line-clamp-2 w-full text-left leading-snug"
                >
                  {name}
                </TextWinds>
              </div>

              <div className="mt-auto pt-2">
                <div className="flex flex-row items-center justify-end">
                  <TextWinds type="content_body3" className="font-bold">
                    {meta.currentPrice}
                  </TextWinds>
                  <TextWinds type="content_body4">원</TextWinds>
                </div>

                <div className="flex flex-row items-center justify-end">
                  <TextWinds
                    type="caption2"
                    color="gray6"
                    className="line-through"
                  >
                    {meta.originPrice}
                  </TextWinds>
                  <TextWinds type="caption2" color="gray6">
                    원
                  </TextWinds>
                </div>
              </div>
            </CardBody>
          </div>
        </Button>
      </Card>
    </>
  );
};

export default BoostupCard;
